'use client';

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/components/auth-provider';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import {
  Banknote,
  Check,
  Gift,
  RefreshCw,
  Scale,
  ShieldAlert,
  Snowflake,
  X,
} from 'lucide-react';

/**
 * Admin money desk (H5 queue, H4 grants, C5 freeze and adjust).
 * Talks to /api/admin/earnings/*. Money surfaces are admin-only.
 */

interface QueueRow {
  id: string;
  amount: string;
  status: string;
  method: string | null;
  taxFormStatus: string;
  screeningStatus: string;
  reviewNote: string | null;
  requestedAt: string;
  paidAt: string | null;
  user: {
    id: string;
    username: string;
    displayName: string | null;
    email: string | null;
    earningsBalance: string;
    earningsFrozenAt: string | null;
  };
  /**
   * Machine-funded provenance (lib/payments/withdrawals.ts,
   * machineFundedPayerFlag): set when the requester's rewards in the hold
   * window trace to a payer funded by a machine purchase. A review hold, not
   * a verdict.
   */
  machineFundedPayers?: {
    flagged: boolean;
    payerCount: number;
    holdDays: number;
    unknown?: boolean;
  };
}

interface GrantRow {
  id: string;
  amount: string;
  reason: string | null;
  status: string;
  publicityConsent: boolean;
  createdAt: string;
  user: { id: string; username: string; displayName: string | null };
}

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'requested', label: 'Requested' },
  { value: 'in_review', label: 'In review' },
  { value: 'paid', label: 'Paid' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'canceled', label: 'Canceled' },
];

const TAX_OPTIONS = ['pending', 'requested', 'received', 'not_required'];
const SCREENING_OPTIONS = ['pending', 'pending_manual', 'clear', 'blocked'];

function dollars(nanocredits: string): string {
  const n = Number(nanocredits) / 1e9;
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Dollars text input to signed nanocredits string (cent precision). */
function toNanocreditsString(dollarsText: string): string | null {
  const d = Number(dollarsText);
  if (!Number.isFinite(d)) return null;
  const cents = Math.round(d * 100);
  return (BigInt(cents) * 10_000_000n).toString();
}

function shortDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

const inputCls =
  'w-full px-3 py-1.5 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary';
const btnCls =
  'px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-accent transition-colors disabled:opacity-50 disabled:pointer-events-none';
const primaryBtnCls =
  'px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 disabled:pointer-events-none';

function SectionCard({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export function EarningsTab() {
  const { user, loading } = useAuth();

  const [statusFilter, setStatusFilter] = useState('');
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [grants, setGrants] = useState<GrantRow[]>([]);
  const [grantsEnabled, setGrantsEnabled] = useState<boolean | null>(null);
  const [grantForm, setGrantForm] = useState({
    userId: '',
    amount: '',
    reason: '',
    publicityConsent: false,
    termsAccepted: false,
  });
  const [grantBusy, setGrantBusy] = useState(false);

  const [freezeForm, setFreezeForm] = useState({ userId: '', reason: '' });
  const [freezeBusy, setFreezeBusy] = useState(false);

  const [adjustForm, setAdjustForm] = useState({ userId: '', amount: '', reason: '' });
  const [adjustBusy, setAdjustBusy] = useState(false);

  const flash = useCallback((msg: string, isError = false) => {
    if (isError) {
      setError(msg);
      setNotice(null);
    } else {
      setNotice(msg);
      setError(null);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`/api/admin/earnings/withdrawals${qs}`);
      if (res.ok) {
        const data = await res.json();
        setQueue(data.requests ?? []);
      }
    } finally {
      setQueueLoading(false);
    }
  }, [statusFilter]);

  const loadGrants = useCallback(async () => {
    const res = await fetch('/api/admin/earnings/grants');
    if (res.status === 403) {
      setGrantsEnabled(false);
      return;
    }
    if (res.ok) {
      const data = await res.json();
      setGrantsEnabled(true);
      setGrants(data.grants ?? []);
    }
  }, []);

  useEffect(() => {
    if (user?.role === 'admin') void loadQueue();
  }, [user?.role, loadQueue]);

  useEffect(() => {
    if (user?.role === 'admin') void loadGrants();
  }, [user?.role, loadGrants]);

  const act = useCallback(
    async (row: QueueRow, action: string, note?: string) => {
      setBusyId(row.id);
      try {
        const res = await fetch(`/api/admin/earnings/withdrawals/${row.id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, ...(note ? { note } : {}) }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          flash(data.error ?? 'Action failed.', true);
        } else {
          flash(action === 'paid' ? 'Marked paid.' : 'Updated.');
        }
        await loadQueue();
      } finally {
        setBusyId(null);
      }
    },
    [flash, loadQueue],
  );

  const patchRow = useCallback(
    async (row: QueueRow, patch: Record<string, string>) => {
      setBusyId(row.id);
      try {
        const res = await fetch(`/api/admin/earnings/withdrawals/${row.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) flash(data.error ?? 'Update failed.', true);
        await loadQueue();
      } finally {
        setBusyId(null);
      }
    },
    [flash, loadQueue],
  );

  const submitGrant = useCallback(async () => {
    const amount = toNanocreditsString(grantForm.amount);
    if (!grantForm.userId || !amount || !grantForm.reason.trim() || !grantForm.termsAccepted) {
      flash('Grants need a user id, an amount, a reason, and confirmed terms acceptance.', true);
      return;
    }
    setGrantBusy(true);
    try {
      const res = await fetch('/api/admin/earnings/grants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: grantForm.userId.trim(),
          amount,
          reason: grantForm.reason.trim(),
          fundTermsAccepted: true,
          publicityConsent: grantForm.publicityConsent,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        flash(data.error ?? 'Grant failed.', true);
      } else {
        flash('Grant credited.');
        setGrantForm({ userId: '', amount: '', reason: '', publicityConsent: false, termsAccepted: false });
        await loadGrants();
      }
    } finally {
      setGrantBusy(false);
    }
  }, [grantForm, flash, loadGrants]);

  const submitFreeze = useCallback(
    async (frozen: boolean) => {
      if (!freezeForm.userId || (frozen && !freezeForm.reason.trim())) {
        flash('Freezing needs a user id and a reason.', true);
        return;
      }
      setFreezeBusy(true);
      try {
        const res = await fetch('/api/admin/earnings/freeze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: freezeForm.userId.trim(),
            frozen,
            reason: freezeForm.reason.trim() || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          flash(data.error ?? 'Freeze update failed.', true);
        } else {
          flash(frozen ? 'Earnings frozen.' : 'Earnings unfrozen.');
          setFreezeForm({ userId: '', reason: '' });
          await loadQueue();
        }
      } finally {
        setFreezeBusy(false);
      }
    },
    [freezeForm, flash, loadQueue],
  );

  const submitAdjust = useCallback(async () => {
    const amount = toNanocreditsString(adjustForm.amount);
    if (!adjustForm.userId || !amount || amount === '0' || !adjustForm.reason.trim()) {
      flash('Adjustments need a user id, a non-zero amount, and a reason.', true);
      return;
    }
    setAdjustBusy(true);
    try {
      const res = await fetch('/api/admin/earnings/adjust', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: adjustForm.userId.trim(),
          amount,
          reason: adjustForm.reason.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        flash(data.error ?? 'Adjustment failed.', true);
      } else {
        flash(`Adjusted. New rewards balance: ${dollars(data.adjustment.newBalance)}.`);
        setAdjustForm({ userId: '', amount: '', reason: '' });
        await loadQueue();
      }
    } finally {
      setAdjustBusy(false);
    }
  }, [adjustForm, flash, loadQueue]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="sm" />
      </div>
    );
  }

  if (user?.role !== 'admin') {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-2">
        <ShieldAlert className="h-6 w-6 mx-auto text-muted-foreground" />
        <p className="text-sm text-muted-foreground">This page is for administrators.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-8">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Earnings administration</h1>
        <button className={btnCls} onClick={() => void loadQueue()}>
          <span className="inline-flex items-center gap-1.5">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </span>
        </button>
      </div>

      {(notice || error) && (
        <div
          className={cn(
            'text-sm rounded-lg border px-3 py-2',
            error
              ? 'border-destructive/40 text-destructive'
              : 'border-border text-muted-foreground',
          )}
        >
          {error ?? notice}
        </div>
      )}

      <SectionCard
        icon={<Banknote className="h-4 w-4 text-muted-foreground" />}
        title="Withdrawal queue"
        subtitle="Requests are reviewed and processed within 5 business days. Payment requires cleared screening and a received tax form."
      >
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                'px-2.5 py-1 text-xs rounded-full border transition-colors',
                statusFilter === f.value
                  ? 'border-primary text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        {queueLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size="xs" />
          </div>
        ) : queue.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No requests.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-3 font-medium">User</th>
                  <th className="py-2 pr-3 font-medium">Amount</th>
                  <th className="py-2 pr-3 font-medium">Requested</th>
                  <th className="py-2 pr-3 font-medium">Tax form</th>
                  <th className="py-2 pr-3 font-medium">Screening</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/60">
                {queue.map((row) => {
                  const busy = busyId === row.id;
                  const open = row.status === 'requested' || row.status === 'in_review';
                  return (
                    <tr key={row.id}>
                      <td className="py-2 pr-3">
                        <div className="font-medium">
                          {row.user.displayName || row.user.username}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Balance {dollars(row.user.earningsBalance)}
                          {row.user.earningsFrozenAt ? ' (frozen)' : ''}
                        </div>
                        {row.machineFundedPayers?.flagged && (
                          <div
                            className="text-xs text-amber-600 dark:text-amber-400"
                            title={`${row.machineFundedPayers.payerCount} payer(s) of this account's rewards bought platform usage with a card in the last ${row.machineFundedPayers.holdDays} days. Hold for review before paying.`}
                          >
                            Machine-funded payer in the last {row.machineFundedPayers.holdDays} days: hold and review
                          </div>
                        )}
                        {row.machineFundedPayers?.unknown && (
                          <div className="text-xs text-muted-foreground">Provenance check unavailable</div>
                        )}
                      </td>
                      <td className="py-2 pr-3 whitespace-nowrap">{dollars(row.amount)}</td>
                      <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">
                        {shortDate(row.requestedAt)}
                      </td>
                      <td className="py-2 pr-3">
                        {open ? (
                          <select
                            className="text-xs rounded-md border border-border bg-background px-1.5 py-1"
                            value={row.taxFormStatus}
                            disabled={busy}
                            onChange={(e) => void patchRow(row, { taxFormStatus: e.target.value })}
                          >
                            {TAX_OPTIONS.map((o) => (
                              <option key={o} value={o}>
                                {o.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {row.taxFormStatus.replace('_', ' ')}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {open ? (
                          <select
                            className="text-xs rounded-md border border-border bg-background px-1.5 py-1"
                            value={row.screeningStatus}
                            disabled={busy}
                            onChange={(e) => void patchRow(row, { screeningStatus: e.target.value })}
                          >
                            {SCREENING_OPTIONS.map((o) => (
                              <option key={o} value={o}>
                                {o.replace('_', ' ')}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {row.screeningStatus.replace('_', ' ')}
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <span className="text-xs">{row.status.replace('_', ' ')}</span>
                        {row.reviewNote && (
                          <div
                            className="text-xs text-muted-foreground max-w-[160px] truncate"
                            title={row.reviewNote}
                          >
                            {row.reviewNote}
                          </div>
                        )}
                      </td>
                      <td className="py-2">
                        <div className="flex flex-wrap gap-1.5">
                          {row.status === 'requested' && (
                            <button
                              className={btnCls}
                              disabled={busy}
                              onClick={() => void act(row, 'in_review')}
                            >
                              Start review
                            </button>
                          )}
                          {row.status === 'in_review' && (
                            <button
                              className={primaryBtnCls}
                              disabled={busy}
                              onClick={() => {
                                if (window.confirm(`Pay ${dollars(row.amount)} to ${row.user.username}? This debits their rewards balance.`)) {
                                  void act(row, 'paid');
                                }
                              }}
                            >
                              <span className="inline-flex items-center gap-1">
                                <Check className="h-3.5 w-3.5" /> Mark paid
                              </span>
                            </button>
                          )}
                          {open && (
                            <>
                              <button
                                className={btnCls}
                                disabled={busy}
                                onClick={() => {
                                  const note = window.prompt('Reason for rejecting this request:');
                                  if (note?.trim()) void act(row, 'rejected', note.trim());
                                }}
                              >
                                Reject
                              </button>
                              <button
                                className={btnCls}
                                disabled={busy}
                                onClick={() => void act(row, 'canceled')}
                              >
                                <span className="inline-flex items-center gap-1">
                                  <X className="h-3.5 w-3.5" /> Cancel
                                </span>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard
        icon={<Gift className="h-4 w-4 text-muted-foreground" />}
        title="Creator Fund grants"
        subtitle="Credits the recipient's rewards balance and records terms acceptance."
      >
        {grantsEnabled === false ? (
          <p className="text-sm text-muted-foreground">Grant crediting is not enabled.</p>
        ) : (
          <>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                className={inputCls}
                placeholder="User id"
                value={grantForm.userId}
                onChange={(e) => setGrantForm((f) => ({ ...f, userId: e.target.value }))}
              />
              <input
                className={inputCls}
                placeholder="Amount (USD)"
                inputMode="decimal"
                value={grantForm.amount}
                onChange={(e) => setGrantForm((f) => ({ ...f, amount: e.target.value }))}
              />
              <input
                className={cn(inputCls, 'sm:col-span-2')}
                placeholder="Reason"
                value={grantForm.reason}
                onChange={(e) => setGrantForm((f) => ({ ...f, reason: e.target.value }))}
              />
            </div>
            <div className="flex flex-col gap-1.5 text-sm">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={grantForm.termsAccepted}
                  onChange={(e) => setGrantForm((f) => ({ ...f, termsAccepted: e.target.checked }))}
                />
                The recipient has accepted the Creator Fund terms.
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={grantForm.publicityConsent}
                  onChange={(e) => setGrantForm((f) => ({ ...f, publicityConsent: e.target.checked }))}
                />
                The recipient consented to publicity.
              </label>
            </div>
            <button className={primaryBtnCls} disabled={grantBusy} onClick={() => void submitGrant()}>
              Credit grant
            </button>

            {grants.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-border">
                      <th className="py-2 pr-3 font-medium">User</th>
                      <th className="py-2 pr-3 font-medium">Amount</th>
                      <th className="py-2 pr-3 font-medium">Reason</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/60">
                    {grants.map((g) => (
                      <tr key={g.id}>
                        <td className="py-2 pr-3">{g.user.displayName || g.user.username}</td>
                        <td className="py-2 pr-3 whitespace-nowrap">{dollars(g.amount)}</td>
                        <td className="py-2 pr-3 max-w-[240px] truncate" title={g.reason ?? ''}>
                          {g.reason}
                        </td>
                        <td className="py-2 pr-3 text-xs">{g.status}</td>
                        <td className="py-2 text-muted-foreground whitespace-nowrap">
                          {shortDate(g.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </SectionCard>

      <div className="grid gap-4 md:grid-cols-2">
        <SectionCard
          icon={<Snowflake className="h-4 w-4 text-muted-foreground" />}
          title="Freeze earnings"
          subtitle="Blocks withdrawals and payouts for the account while frozen."
        >
          <input
            className={inputCls}
            placeholder="User id"
            value={freezeForm.userId}
            onChange={(e) => setFreezeForm((f) => ({ ...f, userId: e.target.value }))}
          />
          <input
            className={inputCls}
            placeholder="Reason (required to freeze)"
            value={freezeForm.reason}
            onChange={(e) => setFreezeForm((f) => ({ ...f, reason: e.target.value }))}
          />
          <div className="flex gap-2">
            <button className={btnCls} disabled={freezeBusy} onClick={() => void submitFreeze(true)}>
              Freeze
            </button>
            <button className={btnCls} disabled={freezeBusy} onClick={() => void submitFreeze(false)}>
              Unfreeze
            </button>
          </div>
        </SectionCard>

        <SectionCard
          icon={<Scale className="h-4 w-4 text-muted-foreground" />}
          title="Adjust earnings"
          subtitle="Signed correction. Use a negative amount for a clawback; the balance may go below zero."
        >
          <input
            className={inputCls}
            placeholder="User id"
            value={adjustForm.userId}
            onChange={(e) => setAdjustForm((f) => ({ ...f, userId: e.target.value }))}
          />
          <input
            className={inputCls}
            placeholder="Amount (USD, negative for clawback)"
            inputMode="decimal"
            value={adjustForm.amount}
            onChange={(e) => setAdjustForm((f) => ({ ...f, amount: e.target.value }))}
          />
          <input
            className={inputCls}
            placeholder="Reason (required)"
            value={adjustForm.reason}
            onChange={(e) => setAdjustForm((f) => ({ ...f, reason: e.target.value }))}
          />
          <button className={btnCls} disabled={adjustBusy} onClick={() => void submitAdjust()}>
            Apply adjustment
          </button>
        </SectionCard>
      </div>
    </div>
  );
}
